import api from '@/utils/api';
import { ApiUrl, Constants } from '@/utils/constants';
import { useEffect, useState } from 'react';
import alertState from '@/states/alertState';
import { useSetRecoilState } from 'recoil';
import { getSigV4HeadersForGet, isExpiredToken } from '@/utils/cognito';
import url from '@/utils/config';
import useSetLoading from '@/hooks/useSetLoading';
import { ConstructionProgressInfoResponse } from '@/types/api/mypage/constructionProgressInfo';
import { AxiosResponse } from 'axios';
import Heading from '../atoms/Heading';
import ExternalLinkButton from '../atoms/ExternalLinkButton';

type Props = {
  anchor: string;
};

/**
 * 開通工事の進捗を取得して、オンライン工事予約への導線とあわせて表示するセクション。
 * トップ画面上で最新状況を見せることで、別導線へ探しに行かず次の手続きへ進める。
 */
const ConstructionProgress = ({ anchor }: Props) => {
  const [constructionProgressInfo, setConstructionProgressInfo] =
    useState<ConstructionProgressInfoResponse>();
  const { startLoading, finishLoading } = useSetLoading();
  const setAlert = useSetRecoilState(alertState);

  useEffect(() => {
    (async () => {
      // 表示時に最新の進捗を取り直し、予約ボタンの遷移先も API の返却値へ合わせる。
      startLoading(ApiUrl.Read.CONSTRUCTION_PROGRESS_INFO);
      const headers = await getSigV4HeadersForGet(ApiUrl.Read.CONSTRUCTION_PROGRESS_INFO);
      await api
        .get(ApiUrl.Read.CONSTRUCTION_PROGRESS_INFO, headers)
        .then((res: AxiosResponse<ConstructionProgressInfoResponse>) => {
          // 見出し文言と導線先 URL は API が持つため、そのまま画面表示 state に反映する。
          setConstructionProgressInfo(res.data);
        })
        .catch((error: any) => {
          if (isExpiredToken(error)) {
            sessionStorage.clear();
            window.location.href = url('/login');
            return;
          }
          // 進捗取得に失敗した場合は個別文言を持たず、共通エラーダイアログへ寄せる。
          setAlert({
            isOpen: true,
            apiType: Constants.ApiType.API_GET,
          });
        })
        .finally(() => finishLoading(ApiUrl.Read.CONSTRUCTION_PROGRESS_INFO));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='m-box-02' id={anchor}>
      <div className='m-box-02__inner'>
        <Heading as='h2' icon='helmet'>
          開通工事の進捗
        </Heading>
        {/* API 側の最新案内文をそのまま見出しと本文に使い、運用変更へ追従できるようにする。 */}
        <Heading as='h3'>{constructionProgressInfo?.appli_title}</Heading>
        <p className='m-text'>{constructionProgressInfo?.appli_text}</p>
        <div className='m-button align-center'>
          <ExternalLinkButton
            id='construction-progress-link'
            variant='secondary'
            href={`${constructionProgressInfo?.const_web_url}`}
          >
            工事日予約や進捗確認
          </ExternalLinkButton>
        </div>
      </div>
    </div>
  );
};

export default ConstructionProgress;
